import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useViewAs } from '../context/ViewAsContext';
import { Button } from './ui/button';
import TopNavMenu from './TopNavMenu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import PortalPicker from './PortalPicker';
import {
  LayoutDashboard,
  FileText,
  Users,
  MessageSquare,
  Award,
  TrendingUp,
  Settings,
  BookOpen,
  Calendar,
  Building2,
  Shield,
  BarChart3,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Home,
  Briefcase,
  Gift,
  Trophy,
  Target,
  DollarSign,
  Upload,
  Palette,
  MessageCircle,
  Phone,
  Megaphone,
  Zap,
  Send,
  CreditCard,
  Bell
} from 'lucide-react';

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { viewAsRole, setViewRole, clearViewRole, getEffectiveRole } = useViewAs();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const effectiveRole = getEffectiveRole(user?.role);

  const getMenuItems = () => {
    if (!user) return [];

    const baseItems = [
      { path: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard className="h-5 w-5" /> },
      { path: '/referrals', label: 'Referrals & Cases', icon: <FileText className="h-5 w-5" /> },
    ];

    // Use effectiveRole instead of user.role
    const roleToUse = effectiveRole;

    // SuperAdmin: Full access to all features
    if (roleToUse === 'superadmin') {
      return [
        ...baseItems,
        { path: '/users', label: 'User Management', icon: <Shield className="h-5 w-5" /> },
        { path: '/doctors', label: 'Referrer Directory', icon: <Users className="h-5 w-5" /> },
        { path: '/provider-comparison', label: 'Provider Comparison', icon: <BarChart3 className="h-5 w-5" /> },
        { path: '/patients', label: 'Patients', icon: <Users className="h-5 w-5" /> },
        { path: '/team', label: 'Team Management', icon: <Users className="h-5 w-5" /> },
        { path: '/deal-rooms', label: 'Deal Rooms', icon: <Briefcase className="h-5 w-5" /> },
        { path: '/analytics', label: 'Analytics', icon: <BarChart3 className="h-5 w-5" /> },
        { path: '/chat', label: 'AI Chat', icon: <MessageSquare className="h-5 w-5" /> },
        { path: '/communications', label: 'Communications', icon: <Phone className="h-5 w-5" /> },
        { path: '/marketing', label: 'Marketing', icon: <Megaphone className="h-5 w-5" /> },
        { path: '/challenges', label: 'Challenges', icon: <Trophy className="h-5 w-5" /> },
        { path: '/rewards', label: 'Rewards', icon: <Gift className="h-5 w-5" /> },
        { path: '/notifications', label: 'Notifications', icon: <Bell className="h-5 w-5" /> },
        { path: '/blog', label: 'Blog', icon: <BookOpen className="h-5 w-5" /> },
        { path: '/community', label: 'Community', icon: <MessageCircle className="h-5 w-5" /> },
        { path: '/courses', label: 'Courses (LMS)', icon: <BookOpen className="h-5 w-5" /> },
        { path: '/subscriptions', label: 'Subscriptions', icon: <CreditCard className="h-5 w-5" /> },
        { path: '/customization', label: 'Customization', icon: <Palette className="h-5 w-5" /> },
        { path: '/settings', label: 'Settings', icon: <Settings className="h-5 w-5" /> },
      ];
    }
    // Admin: Most features except User Management
    else if (roleToUse === 'admin') {
      return [
        ...baseItems,
        { path: '/doctors', label: 'Referrer Directory', icon: <Users className="h-5 w-5" /> },
        { path: '/provider-comparison', label: 'Provider Comparison', icon: <BarChart3 className="h-5 w-5" /> },
        { path: '/patients', label: 'Patients', icon: <Users className="h-5 w-5" /> },
        { path: '/team', label: 'Team Management', icon: <Users className="h-5 w-5" /> },
        { path: '/deal-rooms', label: 'Deal Rooms', icon: <Briefcase className="h-5 w-5" /> },
        { path: '/analytics', label: 'Analytics', icon: <BarChart3 className="h-5 w-5" /> },
        { path: '/chat', label: 'AI Chat', icon: <MessageSquare className="h-5 w-5" /> },
        { path: '/communications', label: 'Communications', icon: <Phone className="h-5 w-5" /> },
        { path: '/marketing', label: 'Marketing', icon: <Megaphone className="h-5 w-5" /> },
        { path: '/challenges', label: 'Challenges', icon: <Trophy className="h-5 w-5" /> },
        { path: '/rewards', label: 'Rewards', icon: <Gift className="h-5 w-5" /> },
        { path: '/notifications', label: 'Notifications', icon: <Bell className="h-5 w-5" /> },
        { path: '/blog', label: 'Blog', icon: <BookOpen className="h-5 w-5" /> },
        { path: '/community', label: 'Community', icon: <MessageCircle className="h-5 w-5" /> },
        { path: '/courses', label: 'Courses (LMS)', icon: <BookOpen className="h-5 w-5" /> },
        { path: '/customization', label: 'Customization', icon: <Palette className="h-5 w-5" /> },
        { path: '/settings', label: 'Settings', icon: <Settings className="h-5 w-5" /> },
      ];
    }
    // Referring Doctor: Limited to their involvement areas
    else if (roleToUse === 'referring_doctor') {
      return [
        ...baseItems,
        { path: '/referrals/new', label: 'New Referral', icon: <FileText className="h-5 w-5" /> },
        { path: '/analytics', label: 'My Analytics', icon: <BarChart3 className="h-5 w-5" /> },
        { path: '/rewards', label: 'Rewards', icon: <Gift className="h-5 w-5" /> },
        { path: '/challenges', label: 'Challenges', icon: <Trophy className="h-5 w-5" /> },
        { path: '/leaderboard', label: 'Leaderboard', icon: <TrendingUp className="h-5 w-5" /> },
        { path: '/community', label: 'Community', icon: <MessageCircle className="h-5 w-5" /> },
        { path: '/courses', label: 'Learning', icon: <BookOpen className="h-5 w-5" /> },
        { path: '/settings', label: 'Settings', icon: <Settings className="h-5 w-5" /> },
      ];
    }
    // Patient: Minimal access (simplified like reference app)
    else if (roleToUse === 'patient') {
      return [
        { path: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard className="h-5 w-5" /> },
        { path: '/referrals', label: 'My Referrals', icon: <FileText className="h-5 w-5" /> },
        { path: '/documents', label: 'Documents', icon: <FileText className="h-5 w-5" /> },
        { path: '/appointments', label: 'Appointments', icon: <Calendar className="h-5 w-5" /> },
        { path: '/education', label: 'Education', icon: <BookOpen className="h-5 w-5" /> },
        { path: '/settings', label: 'Settings', icon: <Settings className="h-5 w-5" /> },
      ];
    }

    return baseItems;
  };

  const menuItems = getMenuItems();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="flex items-center justify-between h-16 px-4">
          {/* Left: Logo and Menu Toggle */}
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="hidden md:flex"
            >
              {sidebarOpen ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>

            <Link to="/" className="flex items-center space-x-2">
              <img src="/logo-3.png" alt="ReferBridge" className="h-8 w-auto" />
            </Link>
          </div>

          {/* Right: User Menu and Notifications */}
          <div className="flex items-center space-x-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="hidden md:flex items-center space-x-2"
            >
              <Home className="h-5 w-5" />
              <span>Home</span>
            </Button>

            <PortalPicker />

            <Button variant="ghost" size="icon" className="relative">
              <Bell className="h-5 w-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </Button>

            {/* SuperAdmin Role Switcher */}
            {(user?.role === 'superadmin' || user?.email === 'wgray@stloralsurgery.com') && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="border-[#D4AF37] text-[#D4AF37]">
                    <Users className="h-4 w-4 mr-2" />
                    {viewAsRole ? `Viewing as ${viewAsRole.replace('_', ' ')}` : 'View As'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => clearViewRole()}>
                    <strong>My View (SuperAdmin)</strong>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setViewRole('admin')}>
                    View as Admin
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setViewRole('referring_doctor')}>
                    View as Referring Doctor
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setViewRole('patient')}>
                    View as Patient
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                  <Avatar>
                    <AvatarImage src={user?.avatar} alt={user?.name} />
                    <AvatarFallback className="bg-[#0A1628] text-[#D4AF37]">
                      {user?.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-2">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                  <p className="text-xs text-[#D4AF37] mt-1 capitalize">
                    {user?.role?.replace('_', ' ')}
                  </p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Top Navigation Menu with Portal Picker */}
      <TopNavMenu />

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`${
            sidebarOpen ? 'w-64' : 'w-0 md:w-20'
          } bg-[#0A1628] text-white transition-all duration-300 ease-in-out fixed md:sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto z-40 ${
            mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
          }`}
        >
          <nav className="p-4 space-y-2">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-[#D4AF37] text-[#0A1628]'
                      : 'text-gray-300 hover:bg-[#1a2332] hover:text-white'
                  }`}
                >
                  <span className={isActive ? 'text-[#0A1628]' : ''}>{item.icon}</span>
                  {(sidebarOpen || mobileMenuOpen) && (
                    <span className="font-medium">{item.label}</span>
                  )}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'md:ml-0' : 'md:ml-0'}`}>
          <div className="p-6">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default DashboardLayout;
